import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { startSession, endSession } from "../../services/GameService";
import GameLayoutTopbar from "../layouts/GameLayoutTopbar";
import GameOverScreen from "../GameOverScreen";
import { usePlayClock } from "../common/usePlayClock";
import fireworksData from "../FindMiddle/animations/fireworks.json";
import { Blossom, PondScenery } from "./PondArt";
import {
  MAX_SCORE,
  SHORT_SIDE,
  advanceDrift,
  boardUnits,
  drawOrder,
  driftSpeed,
  fitBoard,
  makeBlossoms,
  makePond,
  parseLevelParams,
  positionAt,
  remapPoint,
  scoreAfter,
  spreadApart,
  tapResult,
} from "./connectTheDots";
import "./ConnectTheDotsForm.css";

const DEFAULT_GAME_SPEED = 50;
// Space kept below the board if the layout can't be measured (the layout's
// own bottom padding).
const BOARD_BOTTOM_GAP = 16;
// Board units before the board has been measured (and in tests): a square.
const SQUARE = { W: SHORT_SIDE, H: SHORT_SIDE };
// How long a wrong tap's wobble and red ring stay up.
const WRONG_MS = 900;
// Pause on the finished pond (the whole ribbon showing) before the game-over card.
const FINISH_PAUSE_MS = 1400;
// At or below this the score shows in clay red.
const LOW_SCORE = 50;
// Clamp a frame's step so a long or backgrounded frame can't jump the flowers.
const MAX_DT = 0.1;

// The ribbon's polyline points: the centres of the joined blossoms, in order.
const ribbonPoints = (positions, joined) =>
  positions
    .slice(0, joined)
    .map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(" ");

const secondsText = (s) => {
  if (s < 1) return "under a second";
  return s === 1 ? "1 second" : `${s} seconds`;
};

// Connect the Dots — numbered blossoms float on a pond; the player joins them
// in order, 1, 2, 3…, and a marigold ribbon follows. The score counts down from
// 100 by one a second from Start. On higher levels the blossoms drift. The
// player's level sets the count and drift (core/data/game_levels.json).
// `rng` is injectable so tests can make layouts deterministic.
const ConnectTheDotsForm = ({
  speed = DEFAULT_GAME_SPEED,
  gameRoutePath = "connect-the-dots",
  rng = Math.random,
}) => {
  const [phase, setPhase] = useState("loading"); // loading|ready|play|done|over
  const [levelNumber, setLevelNumber] = useState(1);
  const [params, setParams] = useState(() => parseLevelParams(null));
  const [blossoms, setBlossoms] = useState([]);
  const [order, setOrder] = useState([]);
  const [joined, setJoined] = useState(0);
  const [wrongId, setWrongId] = useState(null);
  const [score, setScore] = useState(MAX_SCORE);
  const [finalSeconds, setFinalSeconds] = useState(0);
  const [boardPx, setBoardPx] = useState(null); // {width, height} once measured

  const boardRef = useRef(null);
  const laidOutRef = useRef(null); // board units the current blossoms are laid out in
  const sessionIdRef = useRef(null);
  // The in-flight endSession (it also promotes/demotes the level); a restart
  // waits for it so the next game loads the new level.
  const savingRef = useRef(null);
  // Set by "Start a new game" on the game-over card: the next round starts as
  // soon as its level has loaded, with no second press of Start.
  const autoStartRef = useRef(false);
  const timersRef = useRef([]);
  const wrongTimerRef = useRef(null);
  const startTimeRef = useRef(0); // the score's countdown (wall clock)
  const playClock = usePlayClock(); // play time for the session
  // Per-frame drift data lives in refs so the frame loop never re-renders:
  // it moves the blossoms' <g>s and the ribbon's polylines directly.
  const posRef = useRef([]); // current {x, y} per blossom id
  const driftRef = useRef([]); // {phase, dir} per blossom id
  const joinedRef = useRef(0); // mirror of `joined` for the frame loop
  const blossomEls = useRef([]);
  const ribbonEls = useRef([]);

  const schedule = useCallback((fn, ms) => {
    const t = setTimeout(fn, ms);
    timersRef.current.push(t);
    return t;
  }, []);
  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  // Load the player's level. Anonymous players get Level 1 with no session
  // (startSession handles that).
  const setupGame = useCallback(async () => {
    clearTimers();
    setPhase("loading");
    // Clear the finished pond now, not after the load: a restart waits on the
    // last score's save, and the old flowers shouldn't sit there meanwhile.
    setBlossoms([]);
    setOrder([]);
    joinedRef.current = 0;
    setJoined(0);
    setWrongId(null);
    setScore(MAX_SCORE);
    let level = null;
    // Never carry the last game's (already ended) session into this one.
    sessionIdRef.current = null;
    try {
      if (savingRef.current) await Promise.resolve(savingRef.current).catch(() => {});
      savingRef.current = null;
      const session = await startSession(gameRoutePath);
      sessionIdRef.current = (session && session.sessionId) || null;
      level = session && session.level;
    } catch (e) {
      // play on without a session; the score isn't saved
    }
    // No level back (the server couldn't be reached): keep the level being
    // played rather than dropping to Level 1. A first load stays on the
    // Level 1 defaults the state starts with.
    if (level) {
      setLevelNumber(level.level_number || 1);
      setParams(parseLevelParams(level));
    }
    setPhase("ready");
  }, [gameRoutePath, clearTimers]);

  useEffect(() => {
    setupGame();
    return clearTimers;
  }, [setupGame, clearTimers]);

  // Size the board to the space it has: the game area's full width, and the
  // window's height less what sits above the board (navbar, info bar) and
  // below it (the layout's bottom padding and the footer), so the whole page
  // fits without scrolling. Within the aspect limits (fitBoard). Re-measured
  // when the window resizes or the info bar changes height.
  const boardShown = phase !== "over";
  useLayoutEffect(() => {
    const el = boardRef.current;
    if (!el) return undefined;
    const measure = () => {
      const box = el.getBoundingClientRect();
      const top = box.top + window.scrollY;
      // The layout's padding under the board (the game area wraps the board
      // exactly, so this is just the padding)...
      const layout = el.closest(".game-layout-topbar");
      const padBelow = layout ? layout.getBoundingClientRect().bottom - box.bottom : BOARD_BOTTOM_GAP;
      // ...plus everything after the page's main area: the footer. Measured
      // rather than assumed, since its height changes on small screens. (Not
      // the main area itself: the app shell stretches it to fill the window.)
      const main = el.closest(".app-shell__main");
      let after = 0;
      for (let s = main && main.nextElementSibling; s; s = s.nextElementSibling) {
        after += s.getBoundingClientRect().height;
      }
      const next = fitBoard(
        el.parentElement.clientWidth,
        window.innerHeight - top - padBelow - after,
      );
      setBoardPx((prev) =>
        prev && next && prev.width === next.width && prev.height === next.height ? prev : next,
      );
    };
    measure();
    window.addEventListener("resize", measure);
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(el.parentElement);
      const info = el.closest(".game-layout-topbar");
      const bar = info && info.querySelector(".game-layout-topbar__info");
      if (bar) observer.observe(bar);
    }
    return () => {
      window.removeEventListener("resize", measure);
      if (observer) observer.disconnect();
    };
  }, [boardShown]);

  // The board's units, and so the pond, follow its size.
  const units = boardPx ? boardUnits(boardPx.width, boardPx.height) : SQUARE;
  const pond = useMemo(() => makePond(units.W, units.H), [units.W, units.H]);

  // If the board changes shape mid-round — a window resize, or on a phone the
  // info bar shrinking once play starts — carry the blossoms (and their drift
  // lines) over to the new pond, each keeping its place on the open water. The
  // pond keeps filling the board and nothing ends up on the bank. Done before
  // paint so there's no frame with the old positions on the new pond.
  useLayoutEffect(() => {
    const from = laidOutRef.current;
    if (!from || blossoms.length === 0 || (from.W === pond.W && from.H === pond.H)) return;
    laidOutRef.current = { W: pond.W, H: pond.H };
    const move = remapPoint(makePond(from.W, from.H), pond);
    if (params.movement) {
      posRef.current = posRef.current.map(move);
      setBlossoms(blossoms.map((b) => ({ ...b, start: move(b.start), target: move(b.target) })));
      return;
    }
    // Still blossoms must never overlap (a covered number can't be tapped),
    // and a pond squeezed along one axis can bring two together: nudge apart.
    const spots = spreadApart(pond, blossoms.map((b) => move(b.start)));
    posRef.current = spots.map((p) => ({ ...p }));
    setBlossoms(blossoms.map((b, i) => ({ ...b, start: spots[i], target: spots[i] })));
  }, [pond, blossoms, params.movement]);

  // Lay out a fresh pond and start the countdown.
  const startGame = useCallback(() => {
    laidOutRef.current = { W: pond.W, H: pond.H };
    const laid = makeBlossoms(pond, params.dotsCount, params.movement, rng);
    posRef.current = laid.map((b) => ({ ...b.start }));
    driftRef.current = laid.map(() => ({ phase: 0, dir: 1 }));
    blossomEls.current = [];
    joinedRef.current = 0;
    setJoined(0);
    setWrongId(null);
    setBlossoms(laid);
    setOrder(drawOrder(laid.length, rng));
    setScore(MAX_SCORE);
    startTimeRef.current = Date.now();
    playClock.start();
    setPhase("play");
  }, [pond, params.dotsCount, params.movement, rng, playClock]);

  // From the game-over card: load the next level, then go straight into play.
  const playAgain = () => {
    autoStartRef.current = true;
    setupGame();
  };

  // Once that level is ready, start the round. Before paint, so the Start
  // button never flashes up between rounds.
  useLayoutEffect(() => {
    if (phase !== "ready" || !autoStartRef.current) return;
    autoStartRef.current = false;
    startGame();
  }, [phase, startGame]);

  // The countdown. Setting an unchanged score is a no-op, so this re-renders
  // once a second.
  useEffect(() => {
    if (phase !== "play") return undefined;
    const tick = setInterval(
      () => setScore(scoreAfter(Date.now() - startTimeRef.current)),
      250,
    );
    return () => clearInterval(tick);
  }, [phase]);

  // Drifting levels: move every blossom along its line each frame, by elapsed
  // time, and pull the ribbon along with them. Blossoms may pass over one
  // another briefly; spaced-out starts and the board's room keep each one
  // clear most of the time.
  useEffect(() => {
    if (!params.movement || (phase !== "play" && phase !== "done")) return undefined;
    const unitsPerSec = driftSpeed(speed);
    let rafId;
    let last = null;
    const frame = (ts) => {
      const dt = last == null ? 0 : Math.min((ts - last) / 1000, MAX_DT);
      last = ts;
      blossoms.forEach((b) => {
        const drift = advanceDrift(b, driftRef.current[b.id], unitsPerSec, dt);
        const p = positionAt(b, drift.phase);
        driftRef.current[b.id] = drift;
        posRef.current[b.id] = p;
        const el = blossomEls.current[b.id];
        if (el) el.setAttribute("transform", `translate(${p.x.toFixed(2)} ${p.y.toFixed(2)})`);
      });
      const pts = ribbonPoints(posRef.current, joinedRef.current);
      ribbonEls.current.forEach((el) => el && el.setAttribute("points", pts));
      rafId = requestAnimationFrame(frame);
    };
    rafId = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(rafId);
  }, [phase, params.movement, blossoms, speed]);

  const finishGame = () => {
    const elapsed = Date.now() - startTimeRef.current;
    const playedMs = playClock.stop();
    const finalScore = scoreAfter(elapsed);
    setScore(finalScore);
    setFinalSeconds(Math.floor(elapsed / 1000));
    setPhase("done");
    if (sessionIdRef.current) {
      savingRef.current = endSession(sessionIdRef.current, finalScore, playedMs);
    }
    schedule(() => setPhase("over"), FINISH_PAUSE_MS);
  };

  const handleTap = (id) => {
    if (phase !== "play") return;
    const result = tapResult(id, joinedRef.current);
    if (result === "ignore") return;
    clearTimeout(wrongTimerRef.current);
    if (result === "wrong") {
      setWrongId(id);
      wrongTimerRef.current = schedule(() => setWrongId(null), WRONG_MS);
      return;
    }
    setWrongId(null);
    const next = joinedRef.current + (result === "join" ? 1 : -1);
    joinedRef.current = next;
    setJoined(next);
    if (next === blossoms.length) finishGame();
  };

  const instruction = () => {
    const total = blossoms.length || params.dotsCount;
    switch (phase) {
      case "ready":
        return `Tap the flowers in number order, starting with 1. The quicker you are, the higher your score.${
          params.movement ? " They drift on the water, so keep an eye on them!" : ""
        }`;
      case "play":
        return wrongId !== null
          ? `Not that one — look for flower ${joined + 1}.`
          : `Tap flower ${joined + 1} next (${joined} of ${total} joined).`;
      case "done":
        return "Lovely — every flower is joined! 🌸";
      default:
        return "Getting the pond ready…";
    }
  };

  if (phase === "over") {
    return (
      <GameLayoutTopbar
        className="connect-the-dots"
        infoPanel={<h2 className="heading-secondary">Connect the Dots — Game Over</h2>}
        gameArea={
          <GameOverScreen
            stats={[
              `You joined all ${blossoms.length} flowers in ${secondsText(finalSeconds)}.`,
              `Score: ${score}`,
            ]}
            onRestart={playAgain}
            showFireworks={score > 0}
            fireworksAnimation={fireworksData}
          />
        }
      />
    );
  }

  const ribbon = ribbonPoints(posRef.current, joined);
  const ribbonRef = (i) => (el) => {
    ribbonEls.current[i] = el;
  };

  return (
    <GameLayoutTopbar
      className="connect-the-dots"
      infoPanel={
        <>
          <h2 className="heading-secondary">Level {levelNumber}</h2>
          <h3 className="heading-tertiary">Connect the Dots</h3>
          <p className="ctd-instruction" role="status">
            {instruction()}
          </p>
          <p className={`ctd-score${score > LOW_SCORE ? "" : " ctd-score--low"}`}>
            Score: {score}
          </p>
        </>
      }
      gameArea={
        <div
          className="ctd-board"
          ref={boardRef}
          style={boardPx ? { width: boardPx.width, height: boardPx.height } : undefined}
        >
          <svg
            className="ctd-board__svg"
            viewBox={`0 0 ${units.W} ${units.H}`}
            role="group"
            aria-label="Pond"
          >
            <PondScenery pond={pond} />
            {blossoms.length > 0 && (
              <g className="ctd-ribbon" aria-hidden="true">
                <g transform="translate(0.35 0.7)">
                  <polyline className="ctd-ribbon__shadow" ref={ribbonRef(0)} points={ribbon} />
                </g>
                <polyline className="ctd-ribbon__edge" ref={ribbonRef(1)} points={ribbon} />
                <polyline className="ctd-ribbon__band" ref={ribbonRef(2)} points={ribbon} />
              </g>
            )}
            {order.map((id) => {
              const b = blossoms[id];
              const p = posRef.current[id];
              return (
                <Blossom
                  key={id}
                  ref={(el) => {
                    blossomEls.current[id] = el;
                  }}
                  blossom={b}
                  x={p.x}
                  y={p.y}
                  joined={id < joined}
                  wrong={id === wrongId}
                  onTap={handleTap}
                />
              );
            })}
          </svg>
          {phase === "ready" && (
            <div className="ctd-overlay">
              <button type="button" className="btn btn-primary ctd-start" onClick={startGame}>
                Start
              </button>
            </div>
          )}
        </div>
      }
    />
  );
};

export default ConnectTheDotsForm;
